import React, { useMemo } from 'react'
import { Box, BoxProps, Button, Flex, Grid, useTheme, useToken } from '@chakra-ui/react'
import Link from 'next/link'
import { Edit } from '@emotion-icons/feather'
import { blogAuthorName } from 'entities/blog'
import { canEdit } from 'entities/blog.auth'
import { sortTagsByHighestWeighted } from 'entities/tag'
import { isAdminOrCompanyOwner } from 'entities/user'
import { routes } from 'routes'
import { isServerSide } from 'lib/env'
import useMe from 'hooks/useMe'
import useQuery from 'hooks/useQuery'
import { useVertical, useVerticalConfig, useVerticalUrl } from 'hooks/useVerticalData'
import { useBlogId } from 'containers/blogs/hooks'
import {
    BlogForBlogPage,
    BlogForBlogPageExtended,
    QUERY_RECOMMENDED_ENTITIES,
} from 'containers/blogs/queries'
import * as blogSeo from 'containers/blogs/seo'
import { AboutAuthor } from 'containers/blogs/components/AboutAuthor'
import { BlogInfo } from 'containers/blogs/components/BlogInfo'
import { LikeBlog } from 'containers/blogs/components/LikeBlog'
import { RelatedBlogs } from 'containers/blogs/components/RelatedBlogs'
import { RelatedJobs } from 'containers/blogs/components/job/RelatedJobs'
import { NewsLetterWrapper } from 'containers/blogs/components/newsletter'
import { Heading } from 'components/Heading'
import { HtmlContentWithCode } from 'components/HtmlContent/HtmlContentWithCode'
import AppShell from 'components/appShell'
import { Flair } from 'components/flair/Flair'
import { FeatureImage } from 'components/images/FeatureImage'
import { Seo } from 'components/seo/Seo'
import { Tags } from 'components/tags/Tags'

export const BlogPage = ({ blog }: { blog: BlogForBlogPageExtended }) => {
    const verticalUrl = useVerticalUrl()
    const vertical = useVertical()
    const verticalConfig = useVerticalConfig()
    const blogId = useBlogId()
    const me = useMe()

    const containerWidth = useToken('breakpoints', 'container1')
    const { cardSizes } = useTheme()

    const pauseRelatedEntitiesFetching = isServerSide
    const [{ data: relatedEntities, fetching: relatedEntitiesFetching }] = useQuery({
        query: QUERY_RECOMMENDED_ENTITIES,
        variables: { id: blogId, vertical, pageSizeBlogs: 2, pageSizeJobs: 4 },
        pause: pauseRelatedEntitiesFetching,
    })

    const blogsRelated = relatedEntities?.blogs?.blogs ?? []
    const jobsRelated = relatedEntities?.jobs ?? []

    const {
        tags,
        author,
        authorInfo,
        title,
        feature,
        creationDate,
        readingTime,
        updated,
        primaryVertical,
        upvoteCount,
        seoDescription,
        bodyHtml,
        published,
    } = blog

    const authorName = blogAuthorName(blog)

    const showUnpublishedFlair = isAdminOrCompanyOwner(me, me?.companyId) && !published

    const noIndexAndNoFollow = primaryVertical !== vertical

    const currentUrl =
        verticalUrl +
        routes.article({
            id: blog.id,
        })

    const blogTags = useMemo(() => {
        return sortTagsByHighestWeighted(tags)
    }, [tags])

    return (
        <>
            <AppShell title={title} contentMaxWidth={containerWidth}>
                <Box
                    mx={{
                        base: '16px',
                        sm: '24px',
                        md: '32px',
                        blog_content: '24px',
                        lg: '57px',
                    }}
                    my={{ base: '16px', sm: '24px', md: '32px', lg: '48px', xl: '64px' }}
                >
                    {showUnpublishedFlair && (
                        <Flair variant="alert" mb={{ base: '6px', md: '22px' }}>
                            Unpublished
                        </Flair>
                    )}
                    <Heading
                        as="h1"
                        variant={'blogPageTitle'}
                        mb={{ base: '16px', lg: '22px' }}
                    >
                        {title}
                    </Heading>
                    <BlogInfo
                        creationDate={creationDate}
                        updated={updated}
                        readingTime={readingTime}
                        author={author}
                        authorInfo={authorInfo}
                    />
                    <Box
                        mt={{ base: '16px', md: '27px' }}
                        mb={{ base: '16px', md: '24px' }}
                        mx={{ base: '-16px', sm: '-24px', md: '-32px', blog_content: 0 }}
                    >
                        {feature && (
                            <FeatureImage
                                src={feature}
                                alt={title}
                                sizes="50vw"
                                borderColor="gray.dividers"
                                borderWidth={{ base: 0, blog_content: '1px' }}
                                borderRadius={{ base: 0, blog_content: '16px' }}
                                overflow="hidden"
                            />
                        )}
                    </Box>
                    <Flex
                        direction="column"
                        gridGap={{ base: '48px', blog_content: '64px' }}
                    >
                        <Grid
                            gridTemplateColumns={{
                                base: '100%',
                                blog_content: 'auto minmax(auto, 275px)',
                            }}
                            gridColumnGap={{ blog_content: '40px' }}
                            gridRowGap={{ base: '32px', blog_content: '36px' }}
                            justifyContent={{ base: 'space-evenly', xl: 'space-between' }}
                        >
                            <Box order={{ base: 1, blog_content: 0 }}>
                                <Tags
                                    tags={blogTags}
                                    buildHref={arg => routes.articlesPresetSearch({ tech: arg })}
                                    // wrap props
                                    mb={{ base: '24px', blog_content: '36px' }}
                                    // tag props
                                    variant="secondary"
                                    size="md"
                                />
                                <BlogContent html={bodyHtml} />
                                <LikeBlog
                                    mt={{ base: '36px', blog_content: '48px' }}
                                    mb={{ base: '36px', blog_content: 0 }}
                                    likes={upvoteCount}
                                />
                                {authorInfo && (
                                    <AboutAuthor
                                        name={authorInfo.name}
                                        summary={authorInfo.summary}
                                        imageUrl={authorInfo.imageUrl}
                                        my={{ base: 0, blog_content: '64px' }}
                                        py={{ base: '32px', blog_content: 0 }}
                                        borderTopWidth={{ base: '1px', md: 0 }}
                                        borderBottomWidth={{ base: '1px', md: 0 }}
                                        borderColor="gray.dividers"
                                    />
                                )}
                            </Box>
                            <Box
                                display={{ base: 'contents', blog_content: 'flex' }}
                                flexDirection="column"
                                gridGap="36px"
                            >
                                <EditArticle
                                    blog={blog}
                                    width={{ base: 'min-content', blog_content: '100%' }}
                                />
                                <RelatedJobs
                                    jobs={jobsRelated}
                                    tags={blogTags}
                                    isFetching={
                                        pauseRelatedEntitiesFetching ||
                                        relatedEntitiesFetching
                                    }
                                    order={{ base: 2, blog_content: 0 }}
                                    gridTemplateColumns={{
                                        base: '100%',
                                        md: `repeat(auto-fit, minmax(${cardSizes.sm}, 1fr))`,
                                        blog_content: `100%`,
                                    }}
                                />
                            </Box>
                        </Grid>
                        <RelatedBlogs
                            blogs={blogsRelated}
                            gridTemplateColumns={{
                                base: '100%',
                                sm: `repeat(auto-fill, minmax(${cardSizes.sm}, 1fr))`,
                            }}
                        />
                        <NewsLetterWrapper maxWidth="100%" />
                    </Flex>
                </Box>
            </AppShell>
            <Seo
                title={title}
                description={seoDescription}
                canonical={blogSeo.canonicalUrl(vertical, blog)}
                keywords={blogSeo.keywords(vertical, blog)}
                nofollow={noIndexAndNoFollow}
                noindex={noIndexAndNoFollow}
                author={authorName}
                openGraph={{
                    title: title,
                    url: currentUrl,
                    description: seoDescription,
                    images: [{ url: blogSeo.openGraphImage(blog) }],
                    site_name: verticalConfig.platformName,
                }}
                twitter={{
                    card: 'summary_large_image',
                    site: verticalConfig.twitter,
                    creator: verticalConfig.twitter,
                    pairs: blogSeo.twitterData(blog),
                }}
                structuredData={{
                    '@context': 'http://schema.org',
                    '@type': 'NewsArticle',
                    mainEntityOfPage: {
                        '@type': 'WebPage',
                        '@id': currentUrl,
                    },
                    headline: title,
                    image: feature,
                    datePublished: creationDate,
                    dateModified: updated,
                    author: { '@type': 'Person', name: authorName },
                    publisher: {
                        '@type': 'Organization',
                        name: verticalConfig.platformName,
                        logo: {
                            '@type': 'ImageObject',
                            url: verticalUrl + routes.resource.logo(vertical),
                        },
                    },
                }}
            />
        </>
    )
}

const EditArticle = ({ blog, ...boxProps }: { blog: BlogForBlogPage } & BoxProps) => {
    const me = useMe()
    const id = useBlogId()

    if (!canEdit(blog, me)) {
        return null
    }

    return (
        <Box {...boxProps}>
            <Link href={routes.editArticle({ id })} passHref>
                <Button
                    as="a"
                    variant="primary"
                    size="md"
                    width="100%"
                    leftIcon={<Edit height="16px" width="16px" strokeWidth={2} />}
                >
                    Edit article
                </Button>
            </Link>
        </Box>
    )
}

const BlogContent = ({ html }: { html: string }) => {
    if (html) {
        return <HtmlContentWithCode html={html} />
    }
    return null
}
